import { useMemo } from "react";
import { motion } from "framer-motion";
import { AppShell } from "@/components/AppShell";
import { BentoCard } from "@/components/BentoCard";
import { deriveCostAnalytics } from "@/store/costStore";
import { formatZAR } from "@/store/dashboardStore";
import { useMaterialsStore } from "@/store/materialsStore";

const EASE = [0.22, 1, 0.36, 1] as const;

const card = {
  hidden: { opacity: 0, y: 20 },
  show: (i: number) => ({ opacity: 1, y: 0, transition: { delay: 0.15 + i * 0.09, duration: 0.6, ease: EASE } }),
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">{children}</span>
);

export default function CostAnalyticsScreen() {
  const items = useMaterialsStore((s) => s.items);
  const c = useMemo(() => deriveCostAnalytics(items), [items]);

  return (
    <AppShell>
      <section data-testid="costs-screen" className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Eyebrow>Reports · Money</Eyebrow>
            <h1 data-testid="costs-headline" className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Cost Analytics</h1>
          </div>
          <span data-testid="costs-total" className="font-mono text-xs uppercase tracking-[0.2em] text-cad">Total so far · <span className="text-white">{formatZAR(Math.round(c.total))}</span></span>
        </motion.div>

        <motion.div initial="hidden" animate="show" className="grid grid-cols-1 gap-4 md:grid-cols-6 md:gap-5">
          <motion.div variants={card} custom={0} className="md:col-span-4">
            <BentoCard data-testid="card-cost-breakdown" className="flex h-full flex-col gap-7 p-6 md:p-7">
              <div className="flex flex-col gap-1.5">
                <Eyebrow>Where the money goes</Eyebrow>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-white">Building the shell vs finishing it</h2>
              </div>
              <div className="flex flex-col gap-6">
                {c.groups.map((g, i) => (
                  <div key={g.label} data-testid={`cost-bar-${g.label.toLowerCase()}`} className="flex flex-col gap-2.5">
                    <div className="flex items-baseline justify-between">
                      <span className="font-sans text-sm font-medium text-white">{g.label}</span>
                      <span className="font-mono text-sm tabular-nums text-white/85">
                        {formatZAR(Math.round(g.amount))}
                        <span className="ml-2 text-cad">{Math.round(g.share * 100)}%</span>
                      </span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-cad/25">
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: g.share }}
                        transition={{ delay: 0.5 + i * 0.12, duration: 1, ease: EASE }}
                        style={{ originX: 0 }}
                        className={i === 0 ? "h-full rounded-full bg-safety shadow-[0_0_18px_-4px_rgba(255,91,0,0.7)]" : "h-full rounded-full bg-cad"}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.18em] text-cad">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-safety" />Structural · foundations, walls, roof frame</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-cad" />Finishes · roofing sheets, glass, plaster</span>
              </div>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={1} className="md:col-span-2">
            <BentoCard data-testid="card-confidence" className="flex h-full flex-col justify-between gap-8 p-6 md:p-7">
              <Eyebrow>How sure we are</Eyebrow>
              <div className="flex flex-col gap-2">
                <span data-testid="confidence-value" className="font-display text-5xl font-bold leading-none tracking-tight text-safety sm:text-6xl">{c.confidencePct}%</span>
                <span className="font-sans text-sm leading-relaxed text-white/55">
                  {c.confidencePct >= 85 ? "This estimate should be close to your final cost." : "This estimate is a good guide, but a few things could still change it."}
                </span>
              </div>
            </BentoCard>
          </motion.div>

          <motion.div variants={card} custom={2} className="md:col-span-6">
            <BentoCard data-testid="card-risk-flags" className="flex flex-col gap-5 p-6 md:p-7">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1.5">
                  <Eyebrow>Risk Flags</Eyebrow>
                  <h2 className="font-display text-lg font-semibold tracking-tight text-white">Things worth a second look</h2>
                </div>
                <span data-testid="risk-flag-count" className="flex h-9 min-w-9 items-center justify-center rounded-full border border-safety/50 px-3 font-mono text-sm text-safety">{c.riskFlags.length}</span>
              </div>
              <ul className="flex flex-col divide-y divide-cad/15">
                {c.riskFlags.length === 0 && (
                  <li data-testid="risk-flag-empty" className="py-4 font-sans text-sm text-white/55">Nothing stands out right now. We'll let you know if that changes.</li>
                )}
                {c.riskFlags.map((f, i) => (
                  <motion.li
                    key={f.id}
                    data-testid={`risk-flag-${f.id}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.08, duration: 0.5, ease: EASE }}
                    className="flex gap-4 py-4 first:pt-0 last:pb-0"
                  >
                    <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-safety" />
                    <div className="flex flex-col gap-1">
                      <span className="font-sans text-sm font-medium text-white">{f.text}</span>
                      <span className="font-sans text-sm leading-relaxed text-white/55">{f.detail}</span>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </BentoCard>
          </motion.div>
        </motion.div>
      </section>
    </AppShell>
  );
}
